<template>
  <v-container fluid class="sb-page">
    <!-- Header -->
    <div class="sb-header">
      <div class="sb-header__top">
        <button class="sb-back" @click="redirectBack">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" width="20" height="20">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
          </svg>
        </button>
        <div class="sb-header__info">
          <h1 class="sb-header__title">{{ isEdit ? "Edit Survey" : "Create Survey" }}</h1>
          <span class="sb-header__subtitle">Build a feedback survey for users</span>
        </div>
      </div>
    </div>

    <!-- Validation alert -->
    <AAlertBanner :messages="alertError" hide-title dense />

    <!-- ===== Survey details ===== -->
    <div class="sb-card">
      <h2 class="sb-card__title">Survey Details</h2>

      <div class="sb-field">
        <label class="sb-label">Survey Title <span class="sb-req">*</span></label>
        <v-text-field
          v-model="form.title"
          outlined
          dense
          hide-details
          placeholder="e.g. HRDC Feedback Survey 2026"
        ></v-text-field>
      </div>

      <div class="sb-grid-2">
        <div class="sb-field">
          <label class="sb-label">Active From</label>
          <v-menu
            v-model="menuStart"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
            content-class="sb-datepicker"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="form.startDate"
                outlined
                dense
                hide-details
                readonly
                clearable
                placeholder="Select start date"
                prepend-inner-icon="mdi-calendar"
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="form.startDate"
              no-title
              scrollable
              color="#56bde8"
              @input="menuStart = false"
            ></v-date-picker>
          </v-menu>
        </div>

        <div class="sb-field">
          <label class="sb-label">Active Until</label>
          <v-menu
            v-model="menuEnd"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
            content-class="sb-datepicker"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="form.endDate"
                outlined
                dense
                hide-details
                readonly
                clearable
                placeholder="Select end date"
                prepend-inner-icon="mdi-calendar"
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="form.endDate"
              no-title
              scrollable
              color="#56bde8"
              :min="form.startDate || undefined"
              @input="menuEnd = false"
            ></v-date-picker>
          </v-menu>
        </div>
      </div>
      <p class="sb-hint">
        Users only see the survey while it is active and within this date range.
        Leave a date blank for an open-ended bound.
      </p>

      <div class="sb-switch-row">
        <v-switch
          v-model="form.status"
          inset
          dense
          hide-details
          :color="form.status ? '#16a34a' : '#ef4444'"
          class="mt-0 pt-0 sb-status-switch"
          :class="form.status ? 'sb-status-switch--on' : 'sb-status-switch--off'"
        ></v-switch>
        <span
          class="sb-switch-label"
          :class="form.status ? 'sb-switch-label--on' : 'sb-switch-label--off'"
        >{{ form.status ? "Active" : "Inactive" }}</span>
      </div>
    </div>

    <!-- ===== Welcome banner & message ===== -->
    <div class="sb-card">
      <h2 class="sb-card__title mb-3">Welcome Banner &amp; Message</h2>

      <!-- Banner image (attachment) -->
      <div class="sb-field">
        <label class="sb-label">Banner Image</label>
        <p class="sb-hint mb-2">
          Recommended size <strong>1200 &times; 675px</strong> (16:9 ratio) for a
          crisp, full-width banner. Other sizes are centre-cropped to fit.
        </p>
        <div v-if="form.attachment" class="sb-banner">
          <img :src="attachmentUrl" class="sb-banner__img" alt="Survey banner" />
          <button class="sb-banner__remove" @click="removeAttachment" title="Remove image">
            <v-icon size="18" color="#fff">mdi-close</v-icon>
          </button>
        </div>
        <div
          v-else
          class="sb-upload"
          :class="{ 'sb-upload--busy': uploadingImage }"
          @click="triggerFilePicker"
        >
          <v-progress-circular
            v-if="uploadingImage"
            indeterminate
            size="24"
            color="#56bde8"
          ></v-progress-circular>
          <template v-else>
            <v-icon size="28" color="var(--app-text-faint)">mdi-image-plus</v-icon>
            <span class="sb-upload__text">Click to upload an image</span>
            <span class="sb-upload__hint">PNG, JPG, GIF or WEBP &middot; best at 1200 &times; 675px (16:9)</span>
          </template>
        </div>
        <input
          ref="fileInput"
          type="file"
          accept="image/*"
          class="d-none"
          @change="onFileChange"
        />
      </div>

      <!-- Memorandum (WYSIWYG) -->
      <div class="sb-field">
        <label class="sb-label">Memorandum / Welcome Message</label>
        <tiptap-vuetify
          v-model="form.memorandum"
          :extensions="extensions"
          placeholder="Write a short welcome message shown above the questions..."
          class="sb-editor"
        />
      </div>
    </div>

    <!-- ===== Questions ===== -->
    <div class="sb-card">
      <div class="sb-card__head">
        <h2 class="sb-card__title">Questions</h2>
        <span class="sb-count">{{ form.questions.length }}</span>
      </div>

      <!-- Empty state -->
      <div v-if="form.questions.length === 0" class="sb-empty">
        <v-icon size="40" color="#cbd5e1">mdi-help-circle-outline</v-icon>
        <p>No questions yet. Add one below.</p>
      </div>

      <!-- Question list -->
      <div
        v-for="(q, qIdx) in form.questions"
        :key="q._key"
        class="sb-question"
        :class="{
          'sb-question--dragging': draggingIndex === qIdx,
          'sb-question--drop-before':
            dragOverIndex === qIdx && draggingIndex !== null && draggingIndex > qIdx,
          'sb-question--drop-after':
            dragOverIndex === qIdx && draggingIndex !== null && draggingIndex < qIdx,
        }"
        @dragover.prevent="onDragOver(qIdx)"
        @drop.prevent="onDrop(qIdx)"
      >
        <div class="sb-question__head">
          <span
            class="sb-drag-handle"
            draggable="true"
            title="Drag to reorder"
            @dragstart="onDragStart(qIdx, $event)"
            @dragend="onDragEnd"
          >
            <v-icon size="18">mdi-drag-vertical</v-icon>
          </span>
          <span class="sb-question__badge" :class="`sb-question__badge--${q.answerType}`">
            <v-icon size="14" color="currentColor">{{ typeIcon(q.answerType) }}</v-icon>
            {{ typeLabel(q.answerType) }}
          </span>
          <span class="sb-question__no">Q{{ qIdx + 1 }}</span>
          <div class="sb-question__spacer"></div>
          <button class="sb-icon-btn" :disabled="qIdx === 0" @click="moveQuestion(qIdx, -1)" title="Move up">
            <v-icon size="18">mdi-arrow-up</v-icon>
          </button>
          <button class="sb-icon-btn" :disabled="qIdx === form.questions.length - 1" @click="moveQuestion(qIdx, 1)" title="Move down">
            <v-icon size="18">mdi-arrow-down</v-icon>
          </button>
          <button class="sb-icon-btn sb-icon-btn--danger" @click="removeQuestion(qIdx)" title="Remove question">
            <v-icon size="18">mdi-trash-can-outline</v-icon>
          </button>
        </div>

        <v-text-field
          v-model="q.question"
          outlined
          dense
          hide-details
          placeholder="Type your question..."
          class="mb-2"
        ></v-text-field>

        <!-- Options (radio / checkbox) -->
        <div v-if="q.answerType !== 'textarea'" class="sb-options">
          <div
            v-for="(opt, oIdx) in q.options"
            :key="oIdx"
            class="sb-option"
          >
            <v-icon size="16" color="var(--app-text-faint)">
              {{ q.answerType === "radio" ? "mdi-radiobox-marked" : "mdi-checkbox-marked-outline" }}
            </v-icon>
            <v-text-field
              v-model="q.options[oIdx]"
              outlined
              dense
              hide-details
              :placeholder="`Option ${oIdx + 1}`"
            ></v-text-field>
            <button
              class="sb-icon-btn sb-icon-btn--danger"
              :disabled="q.options.length <= 2"
              @click="removeOption(qIdx, oIdx)"
              title="Remove option"
            >
              <v-icon size="16">mdi-close</v-icon>
            </button>
          </div>
          <button class="sb-add-option" @click="addOption(qIdx)">
            <v-icon size="16">mdi-plus</v-icon> Add option
          </button>
        </div>
      </div>

      <!-- Add question buttons -->
      <div class="sb-add-row">
        <button class="sb-add-btn" @click="addQuestion('radio')">
          <v-icon size="18" color="#56bde8">mdi-radiobox-marked</v-icon>
          Add Single-choice
        </button>
        <button class="sb-add-btn" @click="addQuestion('checkbox')">
          <v-icon size="18" color="#8b5cf6">mdi-checkbox-marked-outline</v-icon>
          Add Multiple-choice
        </button>
      </div>
    </div>

    <!-- ===== Additional comments ===== -->
    <div class="sb-card">
      <div class="sb-switch-row sb-switch-row--between">
        <div>
          <h2 class="sb-card__title mb-1">Include "Additional feedback or comments?"</h2>
          <p class="sb-hint mb-0">Adds a free-text box at the end of the survey.</p>
        </div>
        <v-switch
          v-model="includeComments"
          inset
          dense
          hide-details
          color="#56bde8"
          class="mt-0 pt-0"
        ></v-switch>
      </div>
      <div v-if="includeComments" class="sb-field mt-3">
        <label class="sb-label">Free-text question label</label>
        <v-text-field
          v-model="commentLabel"
          outlined
          dense
          hide-details
          placeholder="Any additional feedback or comments?"
        ></v-text-field>
      </div>
    </div>

    <!-- ===== Actions ===== -->
    <div class="sb-actions">
      <button class="sb-btn sb-btn--primary" :disabled="apiCreate.isLoading || apiUpdate.isLoading" @click="save">
        <v-icon size="18" left>mdi-content-save-outline</v-icon>
        {{ isEdit ? "Save Changes" : "Create Survey" }}
      </button>
    </div>

    <HrdcLoader :loadingDialog="loadingDialog"></HrdcLoader>
  </v-container>
</template>

<script>
import Api from "@/objects/api";
import Model from "@/objects/model";
import Service from "@/objects/service";
import {
  TiptapVuetify,
  Heading,
  Bold,
  Italic,
  Strike,
  Underline,
  Paragraph,
  BulletList,
  OrderedList,
  ListItem,
  Link,
  Blockquote,
  HardBreak,
  HorizontalRule,
  History,
} from "tiptap-vuetify";

export default {
  components: { TiptapVuetify },
  props: ["params"],
  data: () => ({
    apiCreate: new Api(),
    apiUpdate: new Api(),
    apiGetSurvey: new Api(),
    apiUploadAttachment: new Api(),
    model: new Model(),
    service: new Service(),
    loadingDialog: false,
    uploadingImage: false,
    previewUrl: null,
    alertError: [],
    menuStart: false,
    menuEnd: false,
    keySeq: 0,
    draggingIndex: null,
    dragOverIndex: null,
    surveyUuid: null,
    form: {
      title: "",
      status: true,
      attachment: null,
      memorandum: "",
      startDate: null,
      endDate: null,
      questions: [],
    },
    includeComments: false,
    commentLabel: "Any additional feedback or comments?",
    commentUuid: null,
    extensions: [
      History,
      Blockquote,
      Link,
      Underline,
      Strike,
      Italic,
      ListItem,
      BulletList,
      OrderedList,
      [Heading, { options: { levels: [1, 2, 3] } }],
      Bold,
      HorizontalRule,
      Paragraph,
      HardBreak,
    ],
  }),
  computed: {
    isEdit() {
      return Boolean(this.surveyUuid);
    },
    // Banner preview: the just-uploaded image (short-lived SAS from the upload
    // response), or — when editing a saved survey — its stored attachment via
    // the surveyUuid-scoped view endpoint.
    attachmentUrl() {
      if (this.previewUrl) return this.previewUrl;
      if (this.form.attachment && this.surveyUuid) {
        return `${this.$service[this.service.key]}/v1/en/console/survey/view-survey-attachment?surveyUuid=${encodeURIComponent(
          this.surveyUuid
        )}`;
      }
      return null;
    },
  },
  created() {
    this.model.getByKey(this.params.modelKey);
    if (this.model.serviceKey) this.service.set("key", this.model.serviceKey);

    const base = `${this.$service[this.service.key]}/v1/en/console/survey`;
    this.apiCreate.setUrl(`${base}/create`);
    this.apiCreate.setMethod("post");
    this.apiUpdate.setUrl(`${base}/update`);
    this.apiUpdate.setMethod("post");
    this.apiGetSurvey.setUrl(`${base}/get-survey-by-uuid`);
    this.apiUploadAttachment.setUrl(`${base}/upload-attachment`);
    this.apiUploadAttachment.setMethod("post");

    this.surveyUuid = this.params.surveyUuid || null;
    if (this.surveyUuid) {
      this.loadSurvey();
    }
  },
  methods: {
    nextKey() {
      this.keySeq += 1;
      return `q-${this.keySeq}`;
    },
    typeLabel(type) {
      return {
        radio: "Single choice",
        checkbox: "Multiple choice",
        textarea: "Free text",
      }[type];
    },
    typeIcon(type) {
      return {
        radio: "mdi-radiobox-marked",
        checkbox: "mdi-checkbox-marked-outline",
        textarea: "mdi-text-box-outline",
      }[type];
    },
    addQuestion(type) {
      this.form.questions.push({
        _key: this.nextKey(),
        answerType: type,
        question: "",
        options: ["", ""],
      });
    },
    removeQuestion(idx) {
      this.form.questions.splice(idx, 1);
    },
    moveQuestion(idx, dir) {
      const target = idx + dir;
      if (target < 0 || target >= this.form.questions.length) return;
      this.reorderQuestions(idx, target);
    },
    // Move a question and re-insert it at `to` (splice keeps Vue 2 reactivity,
    // unlike index assignment). The question's _key stays stable so its inputs
    // keep their state across the move.
    reorderQuestions(from, to) {
      if (from === to) return;
      const items = this.form.questions;
      const [moved] = items.splice(from, 1);
      items.splice(to, 0, moved);
    },
    // ----- Drag & drop reordering -----
    onDragStart(idx, e) {
      this.draggingIndex = idx;
      e.dataTransfer.effectAllowed = "move";
      // Firefox only starts a drag once data is set.
      try {
        e.dataTransfer.setData("text/plain", String(idx));
      } catch (_) {
        /* noop */
      }
      // Drag the whole question card, not just the small handle.
      const row = e.target.closest(".sb-question");
      if (row && e.dataTransfer.setDragImage) {
        e.dataTransfer.setDragImage(row, 24, 24);
      }
    },
    onDragOver(idx) {
      if (this.draggingIndex === null) return;
      this.dragOverIndex = idx;
    },
    onDrop(idx) {
      const from = this.draggingIndex;
      this.resetDrag();
      if (from === null || from === idx) return;
      this.reorderQuestions(from, idx);
    },
    onDragEnd() {
      this.resetDrag();
    },
    resetDrag() {
      this.draggingIndex = null;
      this.dragOverIndex = null;
    },
    addOption(qIdx) {
      this.form.questions[qIdx].options.push("");
    },
    removeOption(qIdx, oIdx) {
      this.form.questions[qIdx].options.splice(oIdx, 1);
    },
    triggerFilePicker() {
      if (this.uploadingImage) return;
      this.$refs.fileInput.click();
    },
    onFileChange(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = ""; // allow re-selecting the same file later
      if (file) this.uploadAttachment(file);
    },
    uploadAttachment(file) {
      if (!file.type || !file.type.startsWith("image/")) {
        this.$store.dispatch("showMessage", {
          message: "Please select an image file.",
          messageType: "error",
        });
        return;
      }
      const fd = new FormData();
      fd.append("attachment", file);

      this.uploadingImage = true;
      this.apiUploadAttachment.setParams(fd);
      this.apiUploadAttachment.setCallbackCompleted((response) => {
        try {
          const { status, data } = response;
          if (!status) {
            this.$store.dispatch("showMessage", {
              message: typeof data === "string" ? data : "Image upload failed.",
              messageType: "error",
            });
            return;
          }
          this.form.attachment = data.filePath;
          this.previewUrl = data.previewUrl || null;
        } catch (err) {
          console.log(err);
        } finally {
          this.uploadingImage = false;
        }
      });
      this.apiUploadAttachment.fetch();
    },
    removeAttachment() {
      this.form.attachment = null;
      this.previewUrl = null;
    },
    loadSurvey() {
      this.loadingDialog = true;
      this.apiGetSurvey.setQuery(`surveyUuid=${this.surveyUuid}`);
      this.apiGetSurvey.setCallbackCompleted((response) => {
        try {
          const { status, data } = response;
          if (!status || !data) {
            this.$store.dispatch("showMessage", {
              message: "Unable to load survey.",
              messageType: "error",
            });
            return;
          }

          this.form.title = data.title;
          this.form.status = Boolean(data.status);
          this.form.attachment = data.attachment || null;
          this.form.memorandum = data.memorandum || "";
          this.form.startDate = data.timestampStart
            ? data.timestampStart.substring(0, 10)
            : null;
          this.form.endDate = data.timestampEnd
            ? data.timestampEnd.substring(0, 10)
            : null;

          const questions = (data.SurveyQuestion || []).sort(
            (a, b) => (a.sequence ?? a.id) - (b.sequence ?? b.id)
          );
          for (const q of questions) {
            if (q.answerType === "textarea") {
              // Surface the free-text question via the dedicated toggle.
              this.includeComments = true;
              this.commentLabel = q.question;
              this.commentUuid = q.uuid;
              continue;
            }
            this.form.questions.push({
              _key: this.nextKey(),
              uuid: q.uuid,
              answerType: q.answerType,
              question: q.question,
              options:
                (q.SurveyOption || []).map((o) => o.option) || [],
            });
          }
        } catch (err) {
          console.log(err);
        } finally {
          this.loadingDialog = false;
        }
      });
      this.apiGetSurvey.fetch();
    },
    buildPayload() {
      const questions = this.form.questions.map((q) => ({
        uuid: q.uuid,
        question: (q.question || "").trim(),
        answerType: q.answerType,
        options: q.answerType === "textarea"
          ? []
          : q.options.map((o) => (o || "").trim()).filter(Boolean),
      }));

      if (this.includeComments) {
        questions.push({
          uuid: this.commentUuid || undefined,
          question: (this.commentLabel || "").trim() ||
            "Any additional feedback or comments?",
          answerType: "textarea",
          options: [],
        });
      }

      return {
        surveyUuid: this.surveyUuid || undefined,
        title: (this.form.title || "").trim(),
        status: this.form.status,
        attachment: this.form.attachment || null,
        memorandum: this.form.memorandum || null,
        timestampStart: this.form.startDate
          ? `${this.form.startDate}T00:00:00`
          : null,
        timestampEnd: this.form.endDate
          ? `${this.form.endDate}T23:59:59`
          : null,
        questions,
      };
    },
    validate(payload) {
      const errors = [];
      if (!payload.title) errors.push("Survey title is required.");

      const realQuestions = payload.questions.filter(
        (q) => q.answerType !== "textarea"
      );
      if (realQuestions.length === 0)
        errors.push("Add at least one question (single or multiple choice).");

      payload.questions.forEach((q, i) => {
        if (!q.question) errors.push(`Question ${i + 1} is missing its text.`);
        if (
          (q.answerType === "radio" || q.answerType === "checkbox") &&
          q.options.length < 2
        ) {
          errors.push(`Question ${i + 1} needs at least 2 options.`);
        }
      });

      if (
        payload.timestampStart &&
        payload.timestampEnd &&
        new Date(payload.timestampStart) > new Date(payload.timestampEnd)
      ) {
        errors.push("\"Active From\" cannot be after \"Active Until\".");
      }

      return errors;
    },
    save() {
      this.alertError = [];
      const payload = this.buildPayload();
      const errors = this.validate(payload);
      if (errors.length) {
        this.alertError = errors;
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }

      const api = this.isEdit ? this.apiUpdate : this.apiCreate;
      this.loadingDialog = true;
      api.setParams(payload);
      api.setCallbackCompleted((response) => {
        try {
          const { status, data } = response;
          if (!status) {
            this.$store.dispatch("showMessage", {
              message: typeof data === "string" ? data : "Failed to save survey.",
              messageType: "error",
            });
            return;
          }
          this.$store.dispatch("showMessage", {
            message: this.isEdit
              ? "Survey updated successfully!"
              : "Survey created successfully!",
            messageType: "success",
            timeout: 2000,
          });
          this.redirectBack();
        } catch (err) {
          console.log(err);
        } finally {
          this.loadingDialog = false;
        }
      });
      api.fetch();
    },
    redirectBack() {
      this.$router.push({
        name: "HrdcSettingsSurvey",
        params: { modelKey: this.model.key, backTo: "" },
      });
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .sb-page {
  background: var(--app-page);
}

.dark-mode .sb-page >>> .v-text-field--outlined .v-input__slot {
  background: var(--app-field);
}

.dark-mode .sb-page >>> .v-text-field--outlined fieldset {
  border-color: var(--app-field-border) !important;
}

.dark-mode .sb-page >>> .v-text-field--outlined:not(.v-input--is-focused):hover fieldset {
  border-color: var(--app-field-border-hover) !important;
}

.dark-mode .sb-page >>> .v-text-field input,
.dark-mode .sb-page >>> .v-text-field textarea {
  color: var(--app-text);
}

.dark-mode .sb-page >>> .v-text-field input::placeholder,
.dark-mode .sb-page >>> .v-text-field textarea::placeholder {
  color: var(--app-field-placeholder);
}

.dark-mode .sb-page >>> .v-text-field .v-label {
  color: var(--app-field-placeholder);
}

.dark-mode .sb-page >>> .v-text-field .v-input__icon .v-icon {
  color: var(--app-text-faint);
}

.dark-mode .sb-header {
  background: var(--app-elevated);
}

.dark-mode .sb-back {
  background: var(--app-form-tile);
  border: 1px solid var(--app-border-strong);
}

.dark-mode .sb-header__title {
  color: var(--app-accent);
}

.dark-mode .sb-header__subtitle {
  color: var(--app-text-faint);
}

.dark-mode .sb-card {
  background: var(--app-form-tile);
}

.dark-mode .sb-card__title {
  color: var(--app-accent);
}

.dark-mode .sb-count {
  color: var(--app-accent);
}

.dark-mode .sb-hint {
  color: var(--app-text-faint);
}

.dark-mode .sb-upload {
  border: 1.5px dashed var(--app-border-strong);
  background: var(--app-surface);
}

.dark-mode .sb-upload__text {
  color: var(--app-neutral-fg);
}

.dark-mode .sb-upload__hint {
  color: var(--app-text-faint);
}

.dark-mode .sb-switch-label {
  color: var(--app-neutral-fg);
}

.dark-mode .sb-question__badge--checkbox {
  background: var(--app-purple-bg);
}

.dark-mode .sb-question__badge--textarea {
  background: var(--app-success-bg);
}

.dark-mode .sb-question__no {
  color: var(--app-text-muted);
}

.dark-mode .sb-drag-handle {
  color: var(--app-text-faint);
}

.dark-mode .sb-drag-handle:hover {
  color: var(--app-neutral-fg);
  background: var(--app-form-tile-hover);
}

.dark-mode .sb-icon-btn {
  background: var(--app-form-tile);
  border: 1px solid var(--app-border);
  color: var(--app-text-muted);
}

.dark-mode .sb-icon-btn:hover:not(:disabled) {
  background: var(--app-form-tile-hover);
}

.dark-mode .sb-icon-btn--danger:hover:not(:disabled) {
  background: var(--app-danger-bg);
}

.dark-mode .sb-add-btn {
  border: 1px dashed var(--app-border-strong);
  background: var(--app-form-tile);
  color: var(--app-neutral-fg);
}

.dark-mode .sb-empty {
  color: var(--app-text-faint);
}

.dark-mode .sb-btn--ghost {
  background: var(--app-form-tile);
  border: 1px solid var(--app-border-strong);
  color: var(--app-neutral-fg);
}

.dark-mode .sb-btn--ghost:hover {
  background: var(--app-form-tile-hover);
}

.dark-mode .sb-editor >>> .v-card {
  border: 1px solid var(--app-border-strong);
}

.dark-mode .sb-editor >>> .v-toolbar {
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .sb-editor >>> .ProseMirror {
  color: var(--app-text);
}

/* >>> end auto-dark <<< */

.sb-page {
  background: #d8dce2;
  /* Fills main minus the breadcrumb and footer; a `100vh - 64px` guess only
     subtracts the app bar and overflows. See .layout-main__content. */
  flex: 1 0 auto;
  padding: 24px !important;
  padding-bottom: 80px !important;
  font-family: "Lato", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ===== HRDC-themed input fields (modern) =====
   Deep overrides for the Vuetify outlined text-fields used across the form. */
.sb-page >>> .v-text-field--outlined .v-input__slot {
  border-radius: 10px;
  background: #fff;
}
.sb-page >>> .v-text-field--outlined fieldset {
  border-color: #d1d5db !important;
  border-radius: 10px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.sb-page >>> .v-text-field--outlined:not(.v-input--is-focused):hover fieldset {
  border-color: #94a3b8 !important;
}
.sb-page >>> .v-text-field--outlined.v-input--is-focused fieldset {
  border-color: #56bde8 !important;
  border-width: 1px;
  box-shadow: 0 0 0 3px rgba(86, 189, 232, 0.15);
}
.sb-page >>> .v-text-field--outlined.error--text fieldset {
  border-color: #ef4444 !important;
}
.sb-page >>> .v-text-field input,
.sb-page >>> .v-text-field textarea {
  color: #1e293b;
  font-family: "Lato", "Segoe UI", sans-serif;
  font-size: 0.88rem;
}
.sb-page >>> .v-text-field input::placeholder,
.sb-page >>> .v-text-field textarea::placeholder {
  color: #94a3b8;
  opacity: 1;
}
.sb-page >>> .v-text-field .v-label {
  color: #94a3b8;
  font-size: 0.85rem;
}
.sb-page >>> .v-text-field.v-input--is-focused .v-label {
  color: #56bde8;
}
.sb-page >>> .v-text-field .v-input__icon .v-icon {
  color: #94a3b8;
}
.sb-page >>> .v-text-field.v-input--is-focused .v-input__icon .v-icon {
  color: #56bde8;
}
/* Switches inherit the HRDC accent when on */
.sb-page >>> .v-input--switch .v-input--selection-controls__ripple {
  color: #56bde8;
}

/* Header */
.sb-header {
  background: #eaecef;
  border-radius: 16px;
  margin-bottom: 20px;
}
.sb-header__top {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 24px;
}
.sb-back {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid #d1d5db;
  cursor: pointer;
  transition: all 0.2s ease;
}
.sb-back:hover {
  background: #e8f6fc;
  border-color: #56bde8;
}
.sb-header__title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #063058;
  margin: 0;
}
.sb-header__subtitle {
  font-size: 0.78rem;
  color: #94a3b8;
}

/* Card */
.sb-card {
  background: #fff;
  border: 1px solid #e4e8ec;
  border-radius: 14px;
  padding: 22px 24px;
  margin-bottom: 18px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
}
.sb-card__head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}
.sb-card__title {
  font-size: 1rem;
  font-weight: 700;
  color: #063058;
  margin: 0;
}
.sb-count {
  font-size: 0.72rem;
  font-weight: 700;
  color: #063058;
  background: #e8f6fc;
  border-radius: 20px;
  padding: 2px 10px;
}

/* Fields */
.sb-field {
  margin-bottom: 14px;
}
.sb-label {
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  color: #2d3239;
  margin-bottom: 6px;
}
.sb-req {
  color: #ef4444;
}
.sb-grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.sb-hint {
  font-size: 0.74rem;
  color: #94a3b8;
  margin: 4px 0 0;
}

/* Banner upload */
.sb-upload {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 130px;
  border: 1.5px dashed #cbd5e1;
  border-radius: 12px;
  background: #f8fafc;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: center;
  padding: 16px;
}
.sb-upload:hover {
  border-color: #56bde8;
  background: #e8f6fc;
}
.sb-upload--busy {
  cursor: progress;
}
.sb-upload__text {
  font-size: 0.84rem;
  font-weight: 600;
  color: #475569;
}
.sb-upload__hint {
  font-size: 0.72rem;
  color: #94a3b8;
}
.sb-banner {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e4e8ec;
  line-height: 0;
}
.sb-banner__img {
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  display: block;
}
.sb-banner__remove {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: rgba(15, 23, 42, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease;
}
.sb-banner__remove:hover {
  background: #ef4444;
}
.sb-switch-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
}
.sb-switch-row--between {
  justify-content: space-between;
}
.sb-switch-label {
  font-size: 0.82rem;
  font-weight: 600;
  color: #475569;
}
.sb-switch-label--on {
  color: #16a34a;
}
.sb-switch-label--off {
  color: #ef4444;
}

/* Status toggle: green when Active (on), red when Inactive (off).
   Overrides the thumb + track so the off state shows red too (Vuetify's
   color prop only tints the on state). */
.sb-status-switch--on >>> .v-input--switch__track,
.sb-status-switch--on >>> .v-input--switch__thumb {
  color: #16a34a !important;
}
.sb-status-switch--off >>> .v-input--switch__track,
.sb-status-switch--off >>> .v-input--switch__thumb {
  color: #ef4444 !important;
}

/* Question */
.sb-question {
  border: 1px solid #e4e8ec;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 14px;
  background: #fbfdff;
}
.sb-question__head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}
.sb-question__badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.68rem;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 20px;
}
.sb-question__badge--radio {
  background: #e8f6fc;
  color: #075985;
}
.sb-question__badge--checkbox {
  background: #ede9fe;
  color: #7c3aed;
}
.sb-question__badge--textarea {
  background: #dcfce7;
  color: #16a34a;
}
.sb-question__no {
  font-size: 0.75rem;
  font-weight: 700;
  color: #64748b;
}
.sb-question__spacer {
  flex: 1;
}

/* Drag & drop */
.sb-drag-handle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  cursor: grab;
  border-radius: 6px;
  padding: 2px;
  margin-left: -4px;
  touch-action: none;
}
.sb-drag-handle:hover {
  color: #475569;
  background: #eef2f7;
}
.sb-drag-handle:active {
  cursor: grabbing;
}
.sb-question--dragging {
  opacity: 0.5;
  border-color: #56bde8;
}
/* Insertion indicator on the row the cursor is over */
.sb-question--drop-before {
  box-shadow: inset 0 3px 0 -1px #56bde8;
}
.sb-question--drop-after {
  box-shadow: inset 0 -3px 0 -1px #56bde8;
}

/* Options */
.sb-options {
  padding-left: 4px;
}
.sb-option {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.sb-add-option {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #2f9fd1;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px 0;
}
.sb-add-option:hover {
  text-decoration: underline;
}

/* Icon buttons */
.sb-icon-btn {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid #e2e8f0;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s ease;
}
.sb-icon-btn:hover:not(:disabled) {
  background: #f1f5f9;
}
.sb-icon-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.sb-icon-btn--danger:hover:not(:disabled) {
  background: #fee2e2;
  color: #dc2626;
  border-color: #fecaca;
}

/* Add question row */
.sb-add-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 8px;
}
.sb-add-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 10px;
  border: 1px dashed #cbd5e1;
  background: #fff;
  font-size: 0.82rem;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  transition: all 0.15s ease;
}
.sb-add-btn:hover {
  border-color: #56bde8;
  background: #e8f6fc;
  color: #075985;
}

/* Empty */
.sb-empty {
  text-align: center;
  padding: 30px 0;
  color: #94a3b8;
}
.sb-empty p {
  font-size: 0.85rem;
  margin: 8px 0 0;
}

/* Actions */
.sb-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
.sb-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 11px 22px;
  border-radius: 10px;
  border: none;
  font-size: 0.85rem;
  font-weight: 700;
  font-family: "Lato", sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
}
.sb-btn--ghost {
  background: #fff;
  border: 1px solid #d1d5db;
  color: #475569;
}
.sb-btn--ghost:hover {
  background: #f8fafc;
}
.sb-btn--primary {
  background: #56bde8;
  color: #fff;
}
.sb-btn--primary:hover:not(:disabled) {
  background: #3dadd8;
  box-shadow: 0 4px 12px rgba(86, 189, 232, 0.3);
}
.sb-btn--primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* WYSIWYG memorandum editor */
.sb-editor >>> .v-card {
  box-shadow: none !important;
  border: 1px solid #d1d5db;
  border-radius: 10px;
}
.sb-editor >>> .v-toolbar {
  border-bottom: 1px solid #e2e8f0;
  border-radius: 10px 10px 0 0;
}
.sb-editor >>> .v-toolbar__content {
  height: auto !important;
  flex-wrap: wrap;
}
.sb-editor >>> .ProseMirror {
  min-height: 140px;
  padding: 12px 14px;
  font-family: "Lato", sans-serif;
  font-size: 0.9rem;
  color: #1e293b;
}
.sb-editor >>> .ProseMirror:focus {
  outline: none;
}

@media (max-width: 599px) {
  .sb-page {
    padding: 12px 8px !important;
  }
  .sb-grid-2 {
    grid-template-columns: 1fr;
  }
}


/* >>> dark companions: the light rules above pin these to a dark ink
   with !important, which on the dark field renders the text invisible
   rather than absent. Token references only. <<< */

.dark-mode .sb-page >>> .v-text-field input,
.dark-mode .sb-page >>> .v-text-field textarea {
  color: var(--app-text) !important;
}
</style>

<!-- Global (un-scoped): the date-picker renders in a teleported menu, so it
     can't be reached by scoped styles. Selectors are prefixed with the menu's
     content-class (.sb-datepicker) so they only affect this form's pickers. -->
<style>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .sb-datepicker .v-picker,
.dark-mode .sb-datepicker .v-picker__body,
.dark-mode .sb-datepicker .v-date-picker-header {
  background: var(--app-card) !important;
}

.dark-mode .sb-datepicker .v-date-picker-header__value button {
  color: var(--app-accent) !important;
}

.dark-mode .sb-datepicker .v-date-picker-header .v-btn {
  color: var(--app-text-muted) !important;
}

.dark-mode .sb-datepicker .v-date-picker-header .v-btn:hover {
  color: var(--app-accent) !important;
}

.dark-mode .sb-datepicker .v-date-picker-table thead th {
  color: var(--app-text-faint) !important;
}

.dark-mode .sb-datepicker .v-date-picker-table .v-btn {
  color: var(--app-text) !important;
}

.dark-mode .sb-datepicker .v-date-picker-table .v-btn--disabled {
  color: var(--app-text-faint) !important;
}

/* >>> end auto-dark <<< */

.sb-datepicker.v-menu__content {
  border-radius: 14px !important;
  box-shadow: 0 8px 30px rgba(6, 48, 88, 0.15) !important;
  border: none !important;
}
.sb-datepicker .v-picker,
.sb-datepicker .v-picker__body,
.sb-datepicker .v-date-picker-header {
  background: #fff !important;
}
.sb-datepicker .v-date-picker-header__value button {
  font-family: "Lato", sans-serif !important;
  font-weight: 700 !important;
  font-size: 0.95rem !important;
  color: #063058 !important;
}
.sb-datepicker .v-date-picker-header .v-btn {
  color: #64748b !important;
}
.sb-datepicker .v-date-picker-header .v-btn:hover {
  background: #e8f6fc !important;
  color: #063058 !important;
}
.sb-datepicker .v-date-picker-table thead th {
  font-family: "Lato", sans-serif !important;
  font-size: 0.72rem !important;
  font-weight: 600 !important;
  color: #94a3b8 !important;
}
.sb-datepicker .v-date-picker-table .v-btn {
  font-family: "Lato", sans-serif !important;
  font-size: 0.82rem !important;
  font-weight: 500 !important;
  color: #334155 !important;
}
.sb-datepicker .v-date-picker-table .v-btn:hover {
  background: #e8f6fc !important;
}
/* Today indicator */
.sb-datepicker .v-date-picker-table .v-btn.v-date-picker-table__current {
  border-color: #56bde8 !important;
  color: #56bde8 !important;
}
/* Selected day */
.sb-datepicker .v-date-picker-table .v-btn--active {
  background: #56bde8 !important;
  border-color: #56bde8 !important;
  color: #fff !important;
  font-weight: 700 !important;
}
.sb-datepicker .v-date-picker-table .v-btn--active:hover {
  background: #3dadd8 !important;
}
.sb-datepicker .v-date-picker-table .v-btn--disabled {
  color: #cbd5e1 !important;
}
/* Year picker (when changing year) */
.sb-datepicker .v-date-picker-years li.active {
  color: #56bde8 !important;
  font-size: 1.4rem;
}
.sb-datepicker .v-picker__actions .v-btn {
  color: #56bde8 !important;
}
</style>
